import { useEffect, useState } from 'react'
import { Car } from 'lucide-react'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { useGetManagerOptions } from '../../hooks/backend/drivers'
import { useSetVehicleManager } from '../../hooks/backend/fleet'
import { runConfirmed } from '../../hooks/confirmedWrites'
import { writeErrorMessage } from '../../utils/writeError'
import type { ManagerOption } from '../../utils/managers'

// The car's manager. Sits on the CAR HEADER on Vehicle Allocation and on Fleet,
// labelled "Car managed by" so it is never confused with the driver control on
// the seats below it.
//
// Changing this does not move any driver. Each affected seat will show a
// mismatch badge until a human accepts it, because the driver's own row is the
// record of truth for who manages that person.

export function VehicleManagerControl({
  vehicleId,
  plate,
  managerPersonId,
  operational,
  canEdit,
  onChanged,
  className,
}: {
  vehicleId: string
  plate: string
  managerPersonId: string | null
  operational?: boolean
  canEdit: boolean
  onChanged: () => void
  className?: string
}) {
  const options = useGetManagerOptions()
  const setVehicleManager = useSetVehicleManager()
  const [busy, setBusy] = useState(false)
  // What the operator chose, held for exactly as long as the request is in
  // flight. Without it a controlled select paints the new name, re-renders with
  // the row's old value a tick later, and then changes again when the board
  // reloads: three states for one click, and the middle one reads as failure.
  const [pending, setPending] = useState<string | null>(null)

  const optionsTrigger = options.trigger
  useEffect(() => {
    void optionsTrigger({})
  }, [optionsTrigger])

  const list = (options.data as ManagerOption[] | undefined) ?? []

  async function handleSelect(nextManagerId: string) {
    if (!nextManagerId || nextManagerId === managerPersonId) return
    setBusy(true)
    setPending(nextManagerId)
    try {
      // Shown only once the server has answered, and then held until the board
      // has been re-read, so there is no second flicker between the two. Both
      // reads that carry a car's manager are covered, under the field name each
      // one uses, so the car header and the driver drawer never disagree.
      const nextManagerName =
        list.find((o) => o.manager_person_id === nextManagerId)?.manager_name ?? null
      const result = (await runConfirmed(
        () =>
          setVehicleManager.trigger({
            vehicle_id: vehicleId,
            manager_person_id: nextManagerId,
            reason: null,
          }).result,
        () => ({
          label: 'car manager',
          routes: ['fleet/getVehicleManagerBook', 'drivers/getDriverManagerBoard'],
          match: (row: Record<string, unknown>) => row.vehicle_id === vehicleId,
          set: (_row: Record<string, unknown>, route: string) =>
            route === 'fleet/getVehicleManagerBook'
              ? { manager_person_id: nextManagerId, manager_name: nextManagerName }
              : {
                  vehicle_manager_person_id: nextManagerId,
                  vehicle_manager_name: nextManagerName,
                },
        }),
      )) as { message?: string } | null
      toast.success(result?.message ?? `${plate} moved.`)
      onChanged()
    } catch (err) {
      toast.error('Could not change this car’s manager', {
        description: writeErrorMessage(err),
      })
    } finally {
      setBusy(false)
      setPending(null)
    }
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
        <Car className="h-3 w-3" />
        Car managed by
      </span>
      <NativeSelect
        aria-label={`Manager for ${plate}`}
        value={pending ?? managerPersonId ?? ''}
        disabled={!canEdit || busy || options.loading}
        title={busy ? 'Saving. The book updates once the database confirms it.' : undefined}
        onChange={(e) => void handleSelect(e.target.value)}
        className={cn(
          'h-8 text-xs',
          !(pending ?? managerPersonId) && 'border-destructive text-destructive',
        )}
      >
        <option value="">No manager</option>
        {list.map((o) => (
          <option key={o.manager_person_id} value={o.manager_person_id}>
            {o.manager_name}
          </option>
        ))}
      </NativeSelect>
      {busy && <Spinner className="h-3.5 w-3.5" />}
      {operational === false && (
        <span className="text-[11px] text-muted-foreground">
          Off the road, stays on this book
        </span>
      )}
    </div>
  )
}
